"use client";
import type { GameData } from "@/app/games/(details)/types";
import { usePassportProvider } from "@/context";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageAnalytics } from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useCallback, useState } from "react";

interface FollowCTAProps {
  gameData: GameData;
  showOnboardingModal: () => void;
}

export const FollowCTA = ({
  gameData,
  showOnboardingModal,
}: FollowCTAProps) => {
  const [clicked, setClicked] = useState(false);

  const { track } = useGamePageAnalytics();
  const { isWishlisted, addToWishlist, incompleteQuests, onQuestClick } =
    useGameDetailsContext();
  const nextQuest =
    incompleteQuests.length > 0 ? incompleteQuests[0] : undefined;

  const { newOnboardingFlow8BallNov25Gpt } = useFeatureFlags();

  const { isLoggedIn } = usePassportProvider();

  const handleClick = useCallback(async () => {
    setClicked(true);

    track({
      action: Action.PRESSED,
      extras: {
        control: "follow-cta",
        controlLabel: nextQuest && isWishlisted ? "Next Quest" : "Follow Now",
        controlType: ControlType.BUTTON,
      },
    });

    if (!isLoggedIn) {
      if (newOnboardingFlow8BallNov25Gpt) {
        showOnboardingModal();
      } else {
        addToWishlist();
      }
      return;
    }

    if (!isWishlisted) return addToWishlist();

    if (nextQuest) return await onQuestClick(nextQuest);
    const url = gameData?.gameContent?.store_urls?.find((storeUrl) =>
      storeUrl?.includes("store.steampowered.com"),
    );
    if (url) window.open(url, "_blank");
    return;
  }, [
    isLoggedIn,
    nextQuest,
    isWishlisted,
    addToWishlist,
    track,
    onQuestClick,
    gameData,
    showOnboardingModal,
    newOnboardingFlow8BallNov25Gpt,
  ]);

  const handleMouseEnter = useCallback(() => {
    track({
      action: Action.HOVER,
      extras: {
        control: "follow-cta",
        controlType: ControlType.BUTTON,
        controlLabel: nextQuest && isWishlisted ? "Next Quest" : "Follow Now",
      },
    });
  }, [track, nextQuest, isWishlisted]);

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      className={cn(
        "z-50 md:z-auto md:!my-12 group fixed bottom-24 md:bottom-auto md:relative glass-border animated-border rounded-full w-full h-auto max-w-[90%] md:max-w-[28rem] px-8 @lg:px-12 py-2.5 flex items-center justify-between bg-black/35 backdrop-blur-md",
        clicked || isLoggedIn
          ? "md:animate-none"
          : "md:animate-[pulse-glow-strong_4s_ease-in-out_infinite]",
      )}
    >
      <div className="space-y-1 py-2.5 text-start">
        <p className="text-base @lg:text-lg font-medium !leading-none">
          {isWishlisted && nextQuest ? "Next Quest" : "Follow Now"}
        </p>
        <p className="text-xs @lg:text-sm !leading-none">
          {isWishlisted && nextQuest
            ? nextQuest.name
            : "Claim your special prize"}
        </p>
      </div>

      <div className="flex space-x-4 @lg:space-x-8">
        <div className="flex-grow mx-2 w-px bg-[linear-gradient(180deg,rgba(255,255,255,0.00)0%,rgba(255,255,255,0.25)25%,#FFF_75%,rgba(255,255,255,0.00)100%)]" />

        <div className="space-y-1 py-2.5 text-end">
          <p className="text-base @lg:text-lg font-medium !leading-none whitespace-nowrap">
            117 / 10,000
          </p>
          <p className="text-xs @lg:text-sm leading-none">remaining</p>
        </div>
      </div>
      <span
        aria-hidden
        className="md:opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out cta-glow"
      />

      <Image
        src="/assets/steam-onboarding/crate-gold.webp"
        alt="Follow Prize"
        width={132}
        height={124}
        className="opacity-95 w-20 @lg:w-24 absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
      />
    </button>
  );
};
